"use client";

import { RiMusic2Line } from "@remixicon/react";
import { isSetUpcoming } from "@workspace/shared-utils";
import { memo } from "react";
import { Spinner } from "@workspace/ui/components/spinner";
import { useInfiniteScroll } from "@/hooks/shared/use-infinite-scroll";
import type { SetFacepileData } from "@/lib/data/shared/facepile-cached";
import { ArtistSetCard } from "../cards/artist-set-card";
import { ArtistEmptyState, ArtistSearchEmptyState } from "../states/";

// Set type with artist-specific data
interface ArtistSet {
	id: string;
	startTime?: string | null;
	endTime?: string | null;
	artist: {
		id: string;
		name: string;
		slug: string;
		image_url?: string | null;
	};
	festival: {
		id: string;
		name: string;
		slug: string;
		start_date: string;
		end_date: string;
	};
	stage?: {
		id: string;
		name: string;
	} | null;
	friends_set?: boolean;
	special_guests?: string | null;
	is_collaborative?: boolean;
	display_format?: "B2B" | "x" | "+" | "F2F" | "&" | "vs" | "B3B" | "with" | null;
	alias_name?: string | null;
	is_group_alias?: boolean;
	presents_name?: string | null;
	set_artists?: Array<{
		artist_id: string;
		position: number;
		artist_name: string;
	}>;
}

interface ArtistSetsViewProps {
	sets: ArtistSet[];
	artistName: string;
	fetchNextPage: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage: boolean;
	selectedYear: string;
	searchTerm?: string;
	sortBy?: string;
	onClearSearch?: () => void;
	onClearYear?: () => void;
	isActive?: boolean; // For infinite scroll activation
	facepileData?: SetFacepileData;
}

export const ArtistSetsView = memo(function ArtistSetsView({
	sets,
	artistName,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	selectedYear,
	searchTerm = "",
	sortBy = "",
	onClearSearch,
	onClearYear,
	isActive = true,
	facepileData = {},
}: ArtistSetsViewProps) {
	// Enable infinite scroll
	useInfiniteScroll({
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isActive,
	});

	// Note: Navigation is now handled by the ArtistSetCard component using Link

	// Note: Filtering is already done server-side via tRPC query
	// No need to filter client-side

	if (sets.length === 0) {
		const hasFilters = searchTerm.trim() !== "" || selectedYear !== "all";

		if (hasFilters && onClearSearch && onClearYear) {
			return (
				<ArtistSearchEmptyState
					searchTerm={searchTerm}
					selectedYear={selectedYear}
					contentType="sets"
					artistName={artistName}
					onClearSearch={onClearSearch}
					onClearYear={onClearYear}
				/>
			);
		}

		return (
			<ArtistEmptyState
				icon={RiMusic2Line}
				title={
					selectedYear === "all"
						? `${artistName} hasn't performed any sets yet`
						: `${artistName} didn't perform any sets in ${selectedYear}`
				}
				description="When they perform sets at festivals, they'll show up here"
			/>
		);
	}

	// Separate upcoming and past sets using shared utility with grace period
	const upcomingSets = sets.filter((set) => {
		// Use end_time for grace period logic (if available), otherwise fall back to start_time
		return isSetUpcoming({ 
			end_time: set.endTime || set.startTime 
		});
	});
	const pastSets = sets.filter((set) => {
		// Use end_time for grace period logic (if available), otherwise fall back to start_time
		return !isSetUpcoming({ 
			end_time: set.endTime || set.startTime 
		});
	});

	// Deduplicate sets to prevent duplicate keys across sections
	const allSetIds = new Set();
	const deduplicatedUpcoming = upcomingSets.filter((set) => {
		if (allSetIds.has(set.id)) {
			return false;
		}
		allSetIds.add(set.id);
		return true;
	});
	const deduplicatedPast = pastSets.filter((set) => {
		if (allSetIds.has(set.id)) {
			return false;
		}
		allSetIds.add(set.id);
		return true;
	});

	// Determine section order based on sort preference
	const isOldestFirst = sortBy === "date-asc";
	const sections = isOldestFirst
		? [
				{ data: deduplicatedPast, title: "Past", keyPrefix: "past" },
				{
					data: deduplicatedUpcoming,
					title: "Upcoming",
					keyPrefix: "upcoming",
				},
			]
		: [
				{
					data: deduplicatedUpcoming,
					title: "Upcoming",
					keyPrefix: "upcoming",
				},
				{ data: deduplicatedPast, title: "Past", keyPrefix: "past" },
			];

	return (
		<div className="space-y-6">
			{sections.map(
				({ data, title, keyPrefix }) =>
					data.length > 0 && (
						<div key={keyPrefix}>
							<h3 className="mb-4 font-semibold text-foreground text-lg">
								{title}
							</h3>
							<div className="space-y-3">
								{data.map((set) => (
									<ArtistSetCard
										key={`${keyPrefix}-${set.id}`}
										set={set}
										attendees={facepileData[set.id] || []}
										isFacepileLoading={false}
									/>
								))}
							</div>
						</div>
					),
			)}

			{/* Loading indicator for infinite scroll */}
			{isFetchingNextPage && (
				<div className="flex justify-center py-4">
					<Spinner variant="muted" />
				</div>
			)}
		</div>
	);
});

ArtistSetsView.displayName = "ArtistSetsView";
